import {
  DEFAULT_STO_COMPLETION_TIME,
  MIN_STO_COMPLETION_TIME,
} from 'modules/campaigns/lib/constants';
import { AttributionMode } from 'modules/workflows/types';

export enum CampaignStateDefinition {
  Running = 'Running',
  Archived = 'Archived',
  Ready = 'Ready',
}

export const ATTR_MODE_EXPLICITLY_SET = AttributionMode.ExplicitlySet;
export const ATTR_MODE_ATTR_PERIOD = AttributionMode.AttributionPeriod;

export const ATTRIBUTION_MODE_LOOKUP: Record<string | AttributionMode, AttributionMode> = {
  [AttributionMode.LastMessaged]: AttributionMode.AttributionPeriod,
  [AttributionMode.AttributionPeriod]: AttributionMode.AttributionPeriod,
  [AttributionMode.ExplicitlySet]: AttributionMode.ExplicitlySet,
};

export const REVERSE_ATTRIBUTION_MODE_LOOKUP: Record<string | AttributionMode, AttributionMode> = {
  [AttributionMode.AttributionPeriod]: AttributionMode.LastMessaged,
  [AttributionMode.ExplicitlySet]: AttributionMode.ExplicitlySet,
};

export const ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR = {
  ExplicitlySet: {
    prefix: 'Attribute only conversion events with explicitly set',
    suffix: 'field',
    value: ATTR_MODE_EXPLICITLY_SET,
  },
  AttributionPeriod: {
    prefix: 'Last touch attribution within',
    suffix: '',
    value: ATTR_MODE_ATTR_PERIOD,
  },
};

export const ATTR_MODE_OPTIONS_MULTIPLE_CONVERSIONS = [
  {
    prefix: 'Attribute only conversion events with explicitly set',
    suffix: 'field',
    value: ATTR_MODE_EXPLICITLY_SET,
    tooltip:
      "To count as a conversion, an incoming event must have a campaignId field that matches this campaign's ID.",
  },
  {
    prefix: 'Attribute any conversion events received within',
    suffix: '',
    tooltip:
      'To count as a conversion, an incoming event must arrive within the specified hours of when the campaign is sent. If multiple campaigns use the same conversion event, the conversion is assigned to the last one sent.',
    value: ATTR_MODE_ATTR_PERIOD,
  },
];

/** Error Validation */
const HRS_IN_DAY = 24;
const ONE_HOUR = 1;

export const DEFAULT_ATTRIBUTION_PERIOD = HRS_IN_DAY;
export const MAX_ATTRIBUTION_PERIOD = 7 * HRS_IN_DAY;
export const MIN_ATTRIBUTION_PERIOD = ONE_HOUR;
export const MIN_RATE_LIMIT_PER_MINUTE = 120;
export const MIN_RATE_LIMIT_PER_SECOND = 2;
export enum RATE_LIMIT_UNITS {
  MINUTE = 'minute',
  SECOND = 'second',
}

const STO_COMPLETION_TIME_ERROR_MESSAGE = `Must be between ${MIN_STO_COMPLETION_TIME} and ${DEFAULT_STO_COMPLETION_TIME} hours`;
const EVENT_ATT_PERIOD_TIME_ERROR_MESSAGE = `Must be between ${MIN_ATTRIBUTION_PERIOD} and ${MAX_ATTRIBUTION_PERIOD} hours`;
const CONVERSIONS_ATTR_PERIOD_TIME_ERROR_MESSAGE = `Please use a time period greater than ${MIN_ATTRIBUTION_PERIOD} hour and less than ${MAX_ATTRIBUTION_PERIOD} hours`;
const RATE_LIMIT_ERROR_MESSAGE_PER_MINUTE = `Must be at least ${MIN_RATE_LIMIT_PER_MINUTE}`;
const RATE_LIMIT_ERROR_MESSAGE_PER_SECOND = `Must be at least ${MIN_RATE_LIMIT_PER_SECOND}`;

export const STO_COMPLETION_TIME_RANGE = {
  min: MIN_STO_COMPLETION_TIME,
  defaultMessage: STO_COMPLETION_TIME_ERROR_MESSAGE,
  max: DEFAULT_STO_COMPLETION_TIME,
};

export const EVENT_ATT_PERIOD_TIME_RANGE = {
  min: MIN_ATTRIBUTION_PERIOD,
  defaultMessage: EVENT_ATT_PERIOD_TIME_ERROR_MESSAGE,
  max: MAX_ATTRIBUTION_PERIOD,
};

export const CONVERSIONS_ATTR_PERIOD_TIME_RANGE = {
  min: MIN_ATTRIBUTION_PERIOD,
  defaultMessage: CONVERSIONS_ATTR_PERIOD_TIME_ERROR_MESSAGE,
  max: MAX_ATTRIBUTION_PERIOD,
};

export const RATE_LIMIT_RANGE_PER_MINUTE = {
  min: MIN_RATE_LIMIT_PER_MINUTE,
  defaultMessage: RATE_LIMIT_ERROR_MESSAGE_PER_MINUTE,
  max: Infinity,
};

export const RATE_LIMIT_RANGE_PER_SECOND = {
  min: MIN_RATE_LIMIT_PER_SECOND,
  defaultMessage: RATE_LIMIT_ERROR_MESSAGE_PER_SECOND,
  max: Infinity,
};

export const DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS = 24;

/**
 * TEST IDS
 */
export const STO_CHECKBOX_TEST_ID = 'sto-checkbox';
